import React from 'react';
import { Box, TextField, Button, Stack, Alert } from '@mui/material';
import RichTextEditor from '../../Common/RichTextEditor/RichTextEditor';
import { useTheme } from '@mui/material/styles';
 
interface EditorFormProps {
    title?: string;
    onTitleChange?: (val: string) => void;
    description?: string;
    onDescriptionChange?: (val: string) => void;
    onSave?: () => void;
    onCancel?: () => void;
    titlePlaceholder?: string;
    descriptionPlaceholder?: string;
    errorMessage?: string;
}
 
const EditorForm: React.FC<EditorFormProps> = ({
    title = '',
    onTitleChange,
    description = '',
    onDescriptionChange,
    onSave,
    onCancel,
    titlePlaceholder = 'Title',
    descriptionPlaceholder = 'Description',
    errorMessage = '',
}) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#F1F6FB',
                overflow: 'hidden',
                borderTop: `1px solid ${theme.palette.text.subtle}`
            }}
        >
            <Stack spacing={2} sx={{ p: 2 }}>
                {errorMessage && (
                    <Alert severity="error" sx={{ borderRadius: '4px', fontSize: '13px' }}>
                        {errorMessage}
                    </Alert>
                )}
                <TextField
                    fullWidth
                    placeholder={titlePlaceholder}
                    value={title}
                    onChange={(e) => onTitleChange?.(e.target.value)}
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F1F6FB',
                            borderRadius: '4px',
                            '& fieldset': {
                                borderColor: 'rgba(113, 117, 143, 0.8)',
                                borderWidth: '0.8px',
                            },
                            '&:hover fieldset': {
                                borderColor: '#0070D2',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#0070D2',
                                borderWidth: '0.8px',
                                boxShadow: '0 0 0 1px #0070D2',
                            },
                        },
                        '& .MuiInputBase-input': {
                            padding: '12px 16px',
                            fontSize: '14px',
                            color: '#1A1A1A',
                            fontFamily: 'Montserrat, sans-serif',
                            '&::placeholder': {
                                color: '#A9ADCB',
                                opacity: 1,
                                fontStyle: 'italic',
                            },
                        },
                    }}
                />
 
                <RichTextEditor
                    value={description}
                    onChange={onDescriptionChange ?? (() => {})}
                    placeholder={descriptionPlaceholder}
                />
            </Stack>
 
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 3,
                    p: 2,
                    pt: 1,
                    backgroundColor: '#F1F6FB',
                }}
            >
                <Button
                    onClick={onCancel}
                    sx={{
                        textTransform: 'uppercase',
                        color: '#00AEEF',
                        fontWeight: 700,
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                        }
                    }}
                >
                    CANCEL
                </Button>
                <Button
                    variant="contained"
                    onClick={onSave}
                    sx={{
                        backgroundColor: '#0FB1F2',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '14px',
                        px: 4,
                        py: 1,
                        borderRadius: '4px',
                        textTransform: 'uppercase',
                        '&:hover': {
                            backgroundColor: '#00AEEF',
                        }
                    }}
                >
                    SAVE
                </Button>
            </Box>
        </Box>
    );
};
 
export default EditorForm;
